/**
 * JWT Proxy Token Management
 *
 * Handles fetching, storing, and refreshing the JWT used to authenticate
 * requests through the Airtable proxy cloud function.
 */
import { getSession, setSession, removeSession } from "../helpers/storage";
import { DWF_PROXY_TOKEN, DWF_USER_EMAIL } from "../helpers/constants";

let refreshTimer: ReturnType<typeof setInterval> | null = null;

// Use hosting rewrites in production, direct function URL in development
const PROXY_BASE = process.env.REACT_APP_PROXY_BASE_URL || "";

export const getProxyToken = (): string | null => getSession(DWF_PROXY_TOKEN);
export const getStoredEmail = (): string | null => getSession(DWF_USER_EMAIL);

export const storeProxyToken = (token: string) => {
  setSession(DWF_PROXY_TOKEN, token);
};

export const storeEmail = (email: string) => {
  setSession(DWF_USER_EMAIL, email);
};

export const clearProxyToken = () => {
  removeSession(DWF_PROXY_TOKEN);
  removeSession(DWF_USER_EMAIL);
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Call the issueProxyToken cloud function to get a fresh JWT.
 * Used for session restore when a returning user has a userId in localStorage.
 */
export const fetchProxyToken = async (
  userId: string,
  email: string
): Promise<string | null> => {
  try {
    const res = await fetch(`${PROXY_BASE}/api/issue-proxy-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, email }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    storeProxyToken(data.token);
    storeEmail(email);
    return data.token;
  } catch {
    return null;
  }
};

/**
 * Call the issueProxyToken cloud function to get a read-only guest JWT.
 * Used for anonymous visitors browsing before login/registration.
 */
export const fetchGuestToken = async (): Promise<string | null> => {
  try {
    const res = await fetch(`${PROXY_BASE}/api/issue-proxy-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ guest: true }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.token;
  } catch {
    return null;
  }
};

/**
 * Call the loginLookup cloud function to handle the full login flow server-side.
 */
export const loginLookup = async (
  email: string
): Promise<{ user?: any; token?: string; role?: string; error?: string }> => {
  const res = await fetch(`${PROXY_BASE}/api/login-lookup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return res.json();
};

/**
 * Refresh the JWT every 90 minutes (token expires after 2 hours).
 */
export const startTokenRefresh = (userId: string, email: string) => {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(
    () => {
      fetchProxyToken(userId, email);
    },
    90 * 60 * 1000
  );
};

/**
 * Refresh the guest JWT every 90 minutes (token expires after 2 hours).
 */
export const startGuestTokenRefresh = (onToken: (token: string) => void) => {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(
    async () => {
      const token = await fetchGuestToken();
      if (token) onToken(token);
    },
    90 * 60 * 1000
  );
};
